import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Inbox, Globe, FolderOpen, Settings, Plug, Zap, ArrowLeft, Home, GitCommit } from 'lucide-react';
import { UserMenu } from './UserMenu';
import { UsageIndicator } from './UsageIndicator';
import { SyncIndicator } from './SyncIndicator';

// Build-time version info
const commitHash = typeof __COMMIT_HASH__ !== 'undefined' ? __COMMIT_HASH__ : 'dev';
const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : new Date().toISOString();
interface TopBarProps {
  onSearchClick: () => void;
  onInboxClick: () => void;
  onContextStoreClick?: () => void;
  onSettingsClick?: () => void;
  onIntegrationsClick?: () => void;
  attentionCount: number;
  inboxCount?: number;
  isWorkMode?: boolean;
  onWorkModeToggle?: () => void;
}
// Format date for display
const formatBuildTime = (isoString: string) => {
  try {
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
};

export function TopBar({
  onSearchClick,
  onInboxClick,
  onContextStoreClick,
  onSettingsClick,
  onIntegrationsClick,
  attentionCount,
  inboxCount = 2,
  isWorkMode = false,
  onWorkModeToggle
}: TopBarProps) {
  const navigate = useNavigate();
  const [showVersionTooltip, setShowVersionTooltip] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyCommit = async () => {
    try {
      await navigator.clipboard.writeText(commitHash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  return <header className="h-16 border-b border-surface-600 bg-surface-900 flex items-center justify-between px-4 sticky top-0 z-30">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/landing')}
          className="p-2 hover:bg-surface-800 rounded-lg text-text-secondary hover:text-text-primary transition-colors"
          title="Back to Home"
        >
          <Home className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-teal-400">
          <Globe className="w-6 h-6" />
          <span className="font-bold text-lg tracking-tight text-text-primary">
            Nexus<span className="text-teal-400">OS</span>
          </span>
        </div>

        {/* Version indicator */}
        <div className="relative">
          <button
            onMouseEnter={() => setShowVersionTooltip(true)}
            onMouseLeave={() => setShowVersionTooltip(false)}
            onClick={commitHash !== 'unknown' && commitHash !== 'dev' ? handleCopyCommit : undefined}
            className={`flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-text-secondary transition-colors rounded hover:bg-surface-800 ${
              commitHash === 'unknown' || commitHash === 'dev' ? 'cursor-default' : 'cursor-pointer'
            }`}
            title={commitHash !== 'unknown' && commitHash !== 'dev' ? 'Click to copy commit hash' : undefined}
          >
            <GitCommit className="w-3 h-3" />
            <span className="font-mono">{commitHash}</span>
          </button>

          {showVersionTooltip && (
            <div className="absolute top-full left-0 mt-1 px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg shadow-lg z-50 whitespace-nowrap">
              <div className="text-xs text-text-secondary">
                <div className="flex items-center gap-2">
                  <span className="text-text-muted">Commit:</span>
                  <span className="font-mono text-text-primary">{commitHash}</span>
                  {copied && <span className="text-green-400">Copied!</span>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-text-muted">Built:</span>
                  <span className="text-text-primary">{formatBuildTime(buildTime)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onSearchClick} className="flex items-center gap-2 px-3 py-1.5 bg-surface-800 hover:bg-surface-700 rounded-lg text-text-secondary hover:text-text-primary transition-colors border border-surface-500">
          <Search className="w-4 h-4" />
          <span className="text-sm">Search...</span>
          <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-900 border border-surface-500 text-[10px] font-sans">
            ⌘K
          </kbd>
        </button>

        <div className="h-6 w-px bg-surface-800 mx-2" />

        {onWorkModeToggle && <button onClick={onWorkModeToggle} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all border ${isWorkMode ? 'bg-surface-800 text-text-primary border-surface-500 hover:bg-surface-700' : attentionCount > 0 ? 'bg-warning-500/10 text-warning-400 border-warning-500/20 hover:bg-warning-500/20 animate-pulse' : 'bg-surface-800 text-text-muted border-surface-500 hover:bg-surface-700'}`} title={isWorkMode ? 'Back to Overview (⌘⇧A)' : 'Enter Work Mode (⌘⇧A)'}>
            {isWorkMode ? <>
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Overview</span>
              </> : <>
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">{attentionCount}</span>
              </>}
          </button>}

        {!isWorkMode && <>
            {onContextStoreClick && <button onClick={onContextStoreClick} className="p-2 hover:bg-surface-800 rounded-lg text-text-secondary hover:text-text-primary transition-colors" title="Context Store">
                <FolderOpen className="w-5 h-5" />
              </button>}

            <button onClick={onInboxClick} className="p-2 hover:bg-surface-800 rounded-lg text-text-secondary hover:text-text-primary transition-colors relative" title="Knowledge Inbox">
              <Inbox className="w-5 h-5" />
              {inboxCount > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary-500 rounded-full" />}
            </button>

            <div className="h-6 w-px bg-surface-800 mx-2" />

            {onIntegrationsClick && <button onClick={onIntegrationsClick} className="p-2 hover:bg-surface-800 rounded-lg text-text-secondary hover:text-text-primary transition-colors" title="Integrations">
                <Plug className="w-5 h-5" />
              </button>}

            {onSettingsClick && <button onClick={onSettingsClick} className="p-2 hover:bg-surface-800 rounded-lg text-text-secondary hover:text-text-primary transition-colors" title="Settings">
                <Settings className="w-5 h-5" />
              </button>}

            <SyncIndicator />
            <UsageIndicator />
          </>}

        <UserMenu />
      </div>
    </header>;
}